(function () {
  'use strict';

  if (window.DieuxSwiper) return;

  // `frame.js` is a deferred classic script loaded ahead of this one from
  // `snippets/scripts.liquid`, so the scheduler is present by the time this
  // evaluates. If it is ever missing, decline to publish rather than grow a
  // second scheduler and a second length resolver here: every rail already
  // renders a complete natively scrollable static layout, so no enhancement is
  // the designed fallback rather than a broken state.
  const frame = window.DieuxFrame;
  if (!frame) {
    window.reportError?.(new Error('DieuxSwiper requires DieuxFrame; rails stay static'));
    return;
  }

  const loader = document.currentScript;
  const vendorScriptUrl = loader?.dataset.swiperScriptUrl;
  const vendorStyleUrl = loader?.dataset.swiperStyleUrl;
  const reducedMotion = window.DieuxFrame?.motionQuery ?? window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const resizeTargets = new WeakMap();
  const resizeQueue = new Set();
  const settleQueue = new Set();
  const intersectionTargets = new WeakMap();
  const mountedControllers = new Set();
  let modalitySubscribed = false;
  let keyboardRetryTimer;
  let keyboardRetryCount = 0;
  let resizeFrame;
  let settleTimer;
  let vendorPromise;
  let vendorScriptPromise;
  let vendorStylePromise;

  // `thresholdDelta` and `thresholdTime` pace Swiper's own snap path, where one
  // qualifying wheel event steps one slide; they are that path's rate limiter,
  // not an intent test, and a free-mode owner never consults them. Free mode
  // instead moves by `delta * sensitivity`, so this sensitivity is the whole
  // travel scale there and stays 1 to track a trackpad the way a native
  // scroller does. It cannot move a snapping owner: that path applies it only
  // to the position it tests for `releaseOnEdges`.
  const mousewheelDefaults = Object.freeze({
    forceToAxis: true,
    sensitivity: 1,
    thresholdDelta: 12,
    thresholdTime: 200,
    releaseOnEdges: true,
  });

  const defaults = Object.freeze({
    overflowTolerance: 1,
    motionDurationProperty: '--motion-duration-carousel',
    motionDurationFallback: 300,
    unavailableControls: 'hidden',
    watchOverflow: true,
    wheelDominanceRatio: 2.5,
    wheelEngageDelta: 2,
    wheelGestureTimeout: 160,
    // Swiper's own resize path re-reads every slide and re-derives the snap
    // grid on every resize frame, per instance, outside this scheduler. Those
    // two parameters are the vendor's only gates on it: `updateOnWindowResize`
    // binds that handler, and `resizeObserver` chooses whether a per-instance
    // ResizeObserver or a window listener drives it. Both are refused so the
    // shared observer below is the single resize owner, which is what keeps a
    // drag-resize's cost flat as a page gains rails.
    updateOnWindowResize: false,
    resizeObserver: false,
    resizeSettleDelay: 150,
    // Swiper reaches loop mode by relocating real slides across the ends of the
    // track, so a looping rail needs spare slides outside the viewport to move.
    // `loopAdditionalSlides` widens the band it relocates, which moves the seam
    // further off-screen; `loopViewportFactor` is the practical rule for a
    // `slidesPerView: 'auto'` rail, whose visible count the vendor can only
    // resolve at runtime — carry a whole viewport of slides either side of the
    // visible window so the relocation never happens inside it.
    loopAdditionalSlides: 2,
    loopViewportFactor: 2,
    mousewheel: mousewheelDefaults,
    // A range input inside a rail — the video timeline across the foot of a
    // gallery slide — owns any pointer that lands on it, so a drag along it
    // scrubs instead of swiping. `noSwipingSelector` replaces the vendor's
    // class check outright, so its own opt-out class is carried here too.
    noSwipingSelector: '.swiper-no-swiping, input[type="range"]',
    nestedScrollerSelector:
      '.dieux-tabs__rail[data-rail-overflow="true"], '
      + ':is(dieux-carousel, block-sections-carousel) dieux-carousel '
      + '[data-carousel-viewport][data-swiper-overflow="true"]',
  });

  const loopCloneAttribute = 'data-swiper-loop-clone';
  const keyboardRetryDelays = [2000, 4000, 8000];
  const insetPattern = /^inset\(([^)]*)\)/;

  /**
   * Reads the inline-axis insets out of a computed `clip-path`.
   *
   * Only the `inset()` form is understood, because that is the only shape the
   * theme's rails clip with; every other value — `none` included — reports no
   * inset, which leaves the caller with the viewport's own padding box.
   *
   * @param {string} value - Computed `clip-path` value.
   * @returns {{start: number, end: number}} Inline insets in pixels.
   */
  function parseInlineInset(value) {
    const none = { start: 0, end: 0 };
    const match = typeof value === 'string' ? insetPattern.exec(value.trim()) : undefined;
    if (!match) return none;

    const sides = match[1]
      .split(/\bround\b/)[0]
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((side) => Number.parseFloat(side) || 0);
    if (sides.length === 0) return none;

    return { start: sides[3] ?? sides[1] ?? sides[0], end: sides[1] ?? sides[0] };
  }

  function resolveMotionDuration() {
    if (reducedMotion?.matches) return 0;

    const rawDuration = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(defaults.motionDurationProperty)
      .trim();
    const value = Number.parseFloat(rawDuration);

    if (!Number.isFinite(value)) return defaults.motionDurationFallback;
    if (rawDuration.endsWith('ms')) return Math.max(0, value);
    if (rawDuration.endsWith('s')) return Math.max(0, value * 1000);

    return defaults.motionDurationFallback;
  }

  function loadVendorStyle() {
    if (vendorStylePromise) return vendorStylePromise;
    if (!vendorStyleUrl) return Promise.reject(new Error('Missing Swiper stylesheet URL'));

    vendorStylePromise = new Promise(function (resolve, reject) {
      let stylesheet = document.getElementById('dieux-swiper-styles');

      function handleLoad() {
        stylesheet.dataset.loaded = 'true';
        resolve(stylesheet);
      }

      function handleError() {
        stylesheet.remove();
        vendorStylePromise = undefined;
        reject(new Error('Unable to load Swiper stylesheet'));
      }

      if (stylesheet instanceof HTMLLinkElement) {
        if (stylesheet.dataset.loaded === 'true' || stylesheet.sheet) {
          resolve(stylesheet);
          return;
        }

        stylesheet.addEventListener('load', handleLoad, { once: true });
        stylesheet.addEventListener('error', handleError, { once: true });
        return;
      }

      stylesheet = document.createElement('link');
      stylesheet.id = 'dieux-swiper-styles';
      stylesheet.rel = 'stylesheet';
      stylesheet.href = vendorStyleUrl;
      stylesheet.fetchPriority = 'low';
      stylesheet.addEventListener('load', handleLoad, { once: true });
      stylesheet.addEventListener('error', handleError, { once: true });
      document.head.append(stylesheet);
    });

    return vendorStylePromise;
  }

  function loadVendorScript() {
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);
    if (vendorScriptPromise) return vendorScriptPromise;
    if (!vendorScriptUrl) return Promise.reject(new Error('Missing Swiper script URL'));

    vendorScriptPromise = new Promise(function (resolve, reject) {
      let script = document.getElementById('dieux-swiper-script');

      function handleLoad() {
        if (typeof window.Swiper === 'function') {
          script.dataset.loaded = 'true';
          resolve(window.Swiper);
          return;
        }

        handleError();
      }

      function handleError() {
        script.remove();
        vendorScriptPromise = undefined;
        reject(new Error('Unable to load Swiper script'));
      }

      if (script instanceof HTMLScriptElement) {
        if (script.dataset.loaded === 'true') {
          handleLoad();
          return;
        }

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });
        return;
      }

      script = document.createElement('script');
      script.id = 'dieux-swiper-script';
      script.src = vendorScriptUrl;
      script.async = true;
      script.fetchPriority = 'low';
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      document.head.append(script);
    });

    return vendorScriptPromise;
  }

  function loadVendor() {
    if (vendorPromise) return vendorPromise;

    vendorPromise = Promise.all([loadVendorStyle(), loadVendorScript()])
      .then(function () {
        return window.Swiper;
      })
      .catch(function (error) {
        vendorPromise = undefined;
        throw error;
      });

    return vendorPromise;
  }

  function queueResize(controller) {
    resizeQueue.add(controller);
    if (resizeFrame) return;

    resizeFrame = true;
    frame.read(function () {
      const controllers = Array.from(resizeQueue);
      resizeQueue.clear();
      resizeFrame = undefined;
      controllers.forEach(function (queuedController) {
        queuedController.refresh(false);
      });
    });
  }

  /**
   * Defers one live instance's full re-measure until a resize gesture settles.
   *
   * `instance.update()` re-reads every slide, rewrites every margin, and
   * re-derives the snap grid, so running it per resize frame costs the page
   * that work once per live rail per frame. Every rail is `slidesPerView:
   * 'auto'`, so CSS keeps slide widths and gaps correct throughout the drag on
   * its own; only Swiper's own translate bounds and edge state go briefly
   * stale, and both are reconciled here once the pointer stops. The overflow
   * boundary is deliberately not deferred — that check stays in the resize
   * frame so a rail still becomes static or enhanced the moment it crosses.
   *
   * @param {SwiperController} controller - Controller whose instance needs re-measuring.
   * @returns {void}
   */
  function queueSettledUpdate(controller) {
    settleQueue.add(controller);
    if (settleTimer !== undefined) window.clearTimeout(settleTimer);

    settleTimer = window.setTimeout(function () {
      settleTimer = undefined;
      const controllers = Array.from(settleQueue);
      settleQueue.clear();
      // `update()` writes layout, so the whole batch runs in the shared write
      // phase and every rail on the page settles inside one layout pass.
      frame.write(function () {
        controllers.forEach(function (queuedController) {
          queuedController.applySettledUpdate();
        });
      });
    }, defaults.resizeSettleDelay);
  }

  /**
   * Keeps each resizing rail's active slide where it is, in the frame that
   * resized it.
   *
   * Swiper's transform is absolute pixels derived from the geometry it last
   * measured. Deferring that re-measure therefore lets a resizing owner reflow
   * its slides out from under a stale transform: the active slide slides away
   * and its neighbour is revealed until the settle snaps it back. Realignment
   * is only a translate, so it is recovered from live rects rather than by
   * re-measuring every slide.
   *
   * Timing is the whole point of doing it here. A ResizeObserver runs after
   * layout and before paint, so a correction applied inside this callback is
   * painted with the resize that caused it. Deferring to the next frame would
   * leave one frame of drift behind every step of a continuous drag, which is
   * the same visible symptom, merely smaller.
   *
   * @param {Iterable<SwiperController>} controllers - Controllers whose owners just resized.
   * @returns {void}
   */
  function holdActiveSlides(controllers) {
    const corrections = [];

    // Every rail is measured before any rail is written, so a page of rails
    // costs one layout pass here rather than one per rail.
    controllers.forEach(function (controller) {
      const target = controller.measureHoldTranslate();
      if (target !== undefined) corrections.push({ controller, target });
    });

    corrections.forEach(function (correction) {
      correction.controller.applyHoldTranslate(correction.target);
    });
  }

  const sharedResizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(function (entries) {
          const resized = new Set();
          entries.forEach(function (entry) {
            const controller = resizeTargets.get(entry.target);
            if (!controller) return;

            resized.add(controller);
            queueResize(controller);
          });
          holdActiveSlides(resized);
        })
      : undefined;

  const sharedIntersectionObserver =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;

              const controller = intersectionTargets.get(entry.target);
              if (!controller) return;

              sharedIntersectionObserver.unobserve(entry.target);
              intersectionTargets.delete(entry.target);
              controller.scheduleActivation(true);
            });
          },
          { rootMargin: '400px 0px' }
        )
      : undefined;

  function isKeyboardModality() {
    return document.documentElement.dataset.focusModality === 'keyboard';
  }

  function retryVendorForKeyboard() {
    loadVendor().then(
      function () {
        mountedControllers.forEach(function (controller) {
          if (controller.loadFailed && controller.overflows) controller.promote();
        });
      },
      function () {
        if (isKeyboardModality()) scheduleKeyboardRetry();
      }
    );
  }

  function promoteForKeyboard() {
    let vendorFailed = false;
    mountedControllers.forEach(function (controller) {
      if (!controller.overflows) return;
      if (controller.state === 'pending') controller.promote();
      else if (controller.loadFailed) vendorFailed = true;
    });
    if (vendorFailed) retryVendorForKeyboard();
  }

  function scheduleKeyboardRetry() {
    if (keyboardRetryTimer !== undefined || keyboardRetryCount >= keyboardRetryDelays.length) return;

    keyboardRetryTimer = window.setTimeout(function () {
      keyboardRetryTimer = undefined;
      if (isKeyboardModality()) promoteForKeyboard();
    }, keyboardRetryDelays[keyboardRetryCount]);
    keyboardRetryCount += 1;
  }

  function watchKeyboardModality() {
    if (modalitySubscribed) return;
    const focusVisibility = window.DieuxFocusVisibility;
    if (typeof focusVisibility?.subscribe !== 'function') return;

    modalitySubscribed = true;
    focusVisibility.subscribe(function (modality) {
      if (modality === 'keyboard') promoteForKeyboard();
    });
  }

  class SwiperController {
    constructor(configuration) {
      if (!(configuration?.owner instanceof HTMLElement)) {
        throw new TypeError('DieuxSwiper requires an owner element');
      }
      if (!(configuration.viewport instanceof HTMLElement)) {
        throw new TypeError('DieuxSwiper requires a viewport element');
      }

      this.owner = configuration.owner;
      this.viewport = configuration.viewport;
      this.track =
        configuration.track instanceof HTMLElement
          ? configuration.track
          : this.viewport.querySelector('.swiper-wrapper');
      this.stateTarget =
        configuration.stateTarget instanceof HTMLElement
          ? configuration.stateTarget
          : this.owner;
      this.previousButton = configuration.previousButton;
      this.nextButton = configuration.nextButton;
      this.unavailableControls =
        configuration.unavailableControls === 'disabled'
          ? configuration.unavailableControls
          : defaults.unavailableControls;
      this.requiresOverflow = configuration.requiresOverflow !== false;
      // Opt-in, because filling the track is only correct for an owner whose
      // items are safe to repeat. It stays inert for every other owner.
      this.loopFill = configuration.loopFill === true;
      this.loopFillTarget = 0;
      this.loopNavigation = false;
      this.centeredSnapEligible = false;
      this.centeredLoopEngaged = false;
      this.centeredLoopPending = false;
      // A rail's gap is CSS-authored. Naming the custom property here lets the
      // controller own resolving it, seeding `spaceBetween`, and keeping the two
      // in sync on resize, so an owner never measures geometry for a gap and a
      // new rail section inherits that for free.
      this.gapProperty =
        typeof configuration.gapProperty === 'string' ? configuration.gapProperty : undefined;
      this.gapSource =
        configuration.gapSource instanceof HTMLElement ? configuration.gapSource : undefined;
      // Whether the viewport's inline padding is bleed the content may occupy
      // rather than inset it has to stay clear of — a viewport widened past its
      // owner and pulled back by an equal negative margin, or room reserved
      // only while the rail overflows. Such padding belongs to the width the
      // content has, and subtracting it makes a rail that exactly fills its box
      // report overflowing by its own bleed, then keep the verdict alive
      // through the room that verdict opens. Owners that inset for real leave
      // this off and keep the content box.
      this.viewportPaddingIsBleed = configuration.viewportPaddingIsBleed === true;
      this.isNestedScroller = configuration.isNestedScroller === true;
      this.gap = undefined;
      this.edgeAllowance = { start: 0, end: 0 };
      this.measuredWidth = undefined;
      this.measuredHeight = undefined;
      this.options = configuration.options || {};
      this.mousewheelEnabled = configuration.mousewheel === true;
      this.onBeforeRefresh = configuration.onBeforeRefresh;
      this.onStateChange = configuration.onStateChange;
      this.instance = undefined;
      this.state = 'idle';
      this.overflows = false;
      this.mounted = false;
      this.measurementFrame = undefined;
      this.focusFrame = undefined;
      this.pointerFocusPending = false;
      this.pointerFocusResetTimer = undefined;
      this.pointerFocusedElement = undefined;
      this.wheelGestureTime = -Infinity;
      this.pinchLocked = false;
      this.forceUpdatePending = false;
      this.idleHandle = undefined;
      this.idleHandleType = undefined;
      this.loadingPromise = undefined;
      this.loadFailed = false;
      this.fallbackResizeFrame = undefined;
      this.authorInlineStyles = [];
      this.handleIntent = this.promote.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handleWheel = this.handleWheel.bind(this);
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
      this.handleFallbackResize = this.handleFallbackResize.bind(this);
      this.handleLoopControlClick = this.handleLoopControlClick.bind(this);
      this.handlePinch = this.handlePinch.bind(this);
    }

    mount() {
      if (this.mounted) return this;

      this.mounted = true;
      mountedControllers.add(this);
      watchKeyboardModality();
      this.owner.addEventListener('pointerenter', this.handleIntent, { passive: true });
      this.owner.addEventListener('pointerdown', this.handlePointerDown, {
        capture: true,
        passive: true,
      });
      this.owner.addEventListener('focusin', this.handleFocusIn);
      this.owner.addEventListener('keydown', this.handleKeyDown, true);
      this.owner.addEventListener('touchstart', this.handleIntent, { passive: true });
      for (const type of ['touchstart', 'touchend', 'touchcancel']) {
        this.owner.addEventListener(type, this.handlePinch, { capture: true, passive: true });
      }
      if (this.previousButton instanceof HTMLButtonElement) {
        this.previousButton.addEventListener('click', this.handleLoopControlClick);
      }
      if (this.nextButton instanceof HTMLButtonElement) {
        this.nextButton.addEventListener('click', this.handleLoopControlClick);
      }
      if (this.mousewheelEnabled) {
        this.viewport.addEventListener('wheel', this.handleWheel, { passive: true });
      }
      reducedMotion?.addEventListener('change', this.handleMotionPreference);

      if (sharedResizeObserver) {
        resizeTargets.set(this.viewport, this);
        sharedResizeObserver.observe(this.viewport);
        if (this.track) {
          resizeTargets.set(this.track, this);
          sharedResizeObserver.observe(this.track);
        }
      } else {
        window.addEventListener('resize', this.handleFallbackResize, { passive: true });
      }

      this.setState('static');
      this.refresh();
      return this;
    }

    refresh(forceUpdate = true) {
      if (!this.mounted) return;

      this.forceUpdatePending = this.forceUpdatePending || forceUpdate;
      if (this.measurementFrame) return;

      // Keyed on the controller, so a burst of refreshes for one rail measures
      // once, and every rail on the page measures inside a single shared frame.
      this.measurementFrame = true;
      frame.measure(this, () => {
        const shouldUpdateInstance = this.forceUpdatePending;
        this.measurementFrame = undefined;
        this.forceUpdatePending = false;
        if (!this.mounted) return;

        const nextOverflow = this.measureOverflow();
        const sizeChanged = this.syncMeasuredSize();
        const allowanceChanged = this.syncEdgeAllowance();
        const scrollLeft = this.viewport.scrollLeft;
        if (nextOverflow === this.overflows) {
          const instance = this.instance && !this.instance.destroyed ? this.instance : undefined;
          if (!instance) {
            frame.write(() => {
              if (this.mounted) this.publishOverflow(nextOverflow, scrollLeft);
            });
            return;
          }

          // `instance.update()` re-reads every slide's width, so it runs only
          // when something it depends on actually moved. An owner hook that
          // changes nothing must not buy a full re-measure of the rail.
          const gapChanged = this.syncGap();
          const ownerChanged =
            typeof this.onBeforeRefresh === 'function'
            && this.onBeforeRefresh({ controller: this, instance }) === true;
          // Changed content or size can carry the rail across the
          // two-whole-slides boundary that decides centered snapping, and a
          // forced refresh coalesces over any queued settle, so the boundary is
          // re-derived here as well as in the settled update.
          const rebuild =
            shouldUpdateInstance
            && this.centeredSnapEligible
            && this.measureCenteredSnap() !== (instance.params.centeredSlides === true);

          frame.write(() => {
            if (!this.mounted) return;
            this.publishOverflow(nextOverflow, scrollLeft);
            if (instance !== this.instance || instance.destroyed) return;

            // An explicit `refresh()` means the owner's content changed — new
            // slides, a newly activated tab — and must land in this frame.
            // A resize-driven `refresh(false)` settles instead, so a drag pays
            // one re-measure per rail per gesture rather than one per frame.
            if (shouldUpdateInstance) {
              if (rebuild) {
                this.rebuildInstance();
                return;
              }
              instance.update();
              this.updateControls(instance);
            } else if (gapChanged || ownerChanged || sizeChanged || allowanceChanged) {
              queueSettledUpdate(this);
            }
          });
          return;
        }

        this.overflows = nextOverflow;
        if (!nextOverflow) {
          frame.write(() => {
            if (!this.mounted) return;
            this.publishOverflow(false, scrollLeft);
            this.cancelActivation();
            this.destroyInstance();
            this.setState('static');
          });
          return;
        }

        const nearViewport = this.measureNearViewport();
        frame.write(() => {
          if (!this.mounted) return;
          this.publishOverflow(true, scrollLeft);
          this.setState('pending');
          this.scheduleActivation(nearViewport);
        });
      });
    }

    /**
     * Resolves the owner's CSS-authored gap to pixels.
     *
     * The gap is read from the custom property rather than the track's used
     * `column-gap` because the enhanced state zeroes that gap — Swiper supplies
     * the same value as `spaceBetween` instead — so the used value stops being
     * the source of truth the moment enhancement lands. `rem`/`px` tokens
     * resolve arithmetically, so this touches no layout at all.
     *
     * @returns {number} Resolved gap in pixels, or `0` when no gap property is configured.
     */
    resolveGap() {
      if (!this.gapProperty) return 0;

      const source = this.gapSource || this.track || this.owner;
      return frame.resolveCustomProperty(source, this.gapProperty);
    }

    /**
     * Re-resolves the gap and pushes it onto a live instance when it changed.
     *
     * @returns {boolean} Whether the gap moved, and the instance therefore needs a re-measure.
     */
    syncGap() {
      if (!this.gapProperty) return false;

      const gap = this.resolveGap();
      if (gap === this.gap) return false;

      this.gap = gap;
      if (this.instance && !this.instance.destroyed) {
        this.instance.params.spaceBetween = gap;
        this.instance.originalParams.spaceBetween = gap;
      }
      return true;
    }

    /**
     * Records the viewport's current box and reports whether it moved.
     *
     * Runs in the shared measurement phase beside the overflow read, so the
     * settle path never has to measure anything itself.
     *
     * @returns {boolean} Whether the viewport's size changed since the last measurement.
     */
    syncMeasuredSize() {
      const width = this.viewport.clientWidth;
      const height = this.viewport.clientHeight;
      if (width === this.measuredWidth && height === this.measuredHeight) return false;

      this.measuredWidth = width;
      this.measuredHeight = height;
      return true;
    }

    /**
     * Computes the translate that leaves the active slide visually in place.
     *
     * Pure read, paired with `applyHoldTranslate`, so a page of rails can
     * measure before any of them writes.
     *
     * @returns {number|undefined} Target translate, or `undefined` when no correction applies.
     */
    measureHoldTranslate() {
      const instance = this.instance;
      if (!this.mounted || !instance || instance.destroyed) return undefined;
      if (instance.animating) return undefined;
      // Free mode has no anchored active slide; its current translate is the
      // state to preserve until the settled update clamps new bounds.
      if (instance.params.freeMode?.enabled) return undefined;
      // A right-to-left owner resolves its start edge by a different rule and
      // does not ship here; a centered loop has no bounded translate to clamp
      // a midline correction against. Both keep the settle-only correction
      // they had before this hold existed.
      if (instance.rtlTranslate) return undefined;
      const centered = instance.params.centeredSlides === true;
      if (centered && instance.params.loop === true) return undefined;

      const slides = instance.slides;
      const activeSlide = slides?.[instance.activeIndex];
      const firstSlide = slides?.[0];
      const lastSlide = slides?.[slides.length - 1];
      if (!(activeSlide instanceof HTMLElement)) return undefined;
      if (!(firstSlide instanceof HTMLElement) || !(lastSlide instanceof HTMLElement)) return undefined;

      // Every slide carries the same transform, so distances between their
      // rects are the untransformed track geometry and stay correct while the
      // wrapper's own offset is stale.
      const trackStart = firstSlide.getBoundingClientRect().left;
      const contentWidth = lastSlide.getBoundingClientRect().right - trackStart;
      const viewportStyles = window.getComputedStyle(this.viewport);
      const viewportContentWidth = Math.max(
        0,
        this.viewport.clientWidth
          - (Number.parseFloat(viewportStyles.paddingInlineStart) || 0)
          - (Number.parseFloat(viewportStyles.paddingInlineEnd) || 0)
      );
      // Clamping to the end reproduces the vendor's own rule that a rail
      // already at its end stays flush there instead of revealing dead space
      // as the owner widens.
      const maxScroll = Math.max(0, contentWidth - viewportContentWidth);
      const activeRect = activeSlide.getBoundingClientRect();
      const activeOffset = activeRect.left - trackStart;
      // A centered owner keeps the active slide on the viewport's midline; the
      // shared clamp below is the same rule `centeredSlidesBounds` derives its
      // snap grid from, so the first and last slides hold flush at the ends.
      const alignedOffset = centered
        ? activeOffset - (viewportContentWidth - activeRect.width) / 2
        : activeOffset;
      const target = -Math.min(Math.max(0, alignedOffset), maxScroll);

      if (Math.abs(target - instance.translate) <= defaults.overflowTolerance) return undefined;

      return target;
    }

    /**
     * Applies a translate measured by `measureHoldTranslate`. Pure write.
     *
     * @param {number} target - Translate to hold the active slide at.
     * @returns {void}
     */
    applyHoldTranslate(target) {
      const instance = this.instance;
      if (!this.mounted || !instance || instance.destroyed) return;

      // A leftover transition duration from an earlier slide change would
      // animate this correction and reintroduce the drift it exists to remove.
      instance.setTransition(0);
      instance.setTranslate(target);
    }

    /**
     * Applies the deferred re-measure once a resize gesture has settled.
     *
     * `instance.update()` is a superset of the vendor's own resize handler in
     * this Swiper build: it re-evaluates `breakpoints`, re-reads size and
     * slides, re-derives progress and slide classes, clamps the translate for
     * both free and snapping modes, and re-checks overflow.
     *
     * @returns {void}
     */
    applySettledUpdate() {
      if (!this.mounted) return;
      if (!this.instance || this.instance.destroyed) return;
      // The vendor handler skips a zero-width owner so a hidden panel cannot
      // record a collapsed geometry. The same guard reads the size captured in
      // the measurement phase rather than measuring again, which keeps this
      // whole batch inside one layout pass. A rail hidden after it queued is
      // re-measured by the explicit `refresh()` its owner runs on reveal.
      if (this.measuredWidth === 0) return;

      // A widened viewport shows more slides, which raises the number loop mode
      // needs behind and ahead of them. Re-deriving it here is what keeps the
      // seam off-screen after a resize instead of only at the width the rail
      // was first enhanced at.
      if (this.loopFill && this.instance.params?.loop === true) {
        const target = this.measureLoopFillTarget(this.instance.params.slidesPerGroup ?? 1);
        if (target > this.loopFillTarget) {
          this.rebuildInstance();
          return;
        }
      }

      // A resize can carry the rail across the two-whole-slides boundary that
      // decides centered snapping. The alignment rule threads through the snap
      // grid, loop relocation, and progress derivation, so — like the loop
      // fill above — the instance is rebuilt on the slide the customer was
      // looking at rather than mutated in place.
      if (this.centeredSnapEligible) {
        const centeredSnap = this.measureCenteredSnap();
        if (centeredSnap !== (this.instance.params.centeredSlides === true)) {
          this.rebuildInstance();
          return;
        }
      }

      this.instance.update();
      this.updateControls(this.instance);
    }

    /**
     * Resolves the viewport's inline padding to pixels. Pure read.
     *
     * @returns {{start: number, end: number}} Resolved inline padding.
     */
    measureViewportInlinePadding() {
      const viewportStyles = window.getComputedStyle(this.viewport);

      return {
        start: Number.parseFloat(viewportStyles.paddingInlineStart) || 0,
        end: Number.parseFloat(viewportStyles.paddingInlineEnd) || 0,
      };
    }

    /**
     * Resolves the viewport's content box width. Pure read.
     *
     * @returns {number} Viewport width inside its inline padding.
     */
    measureViewportContentWidth() {
      const viewportWidth = this.viewport.clientWidth;
      if (viewportWidth <= 0) return 0;
      if (this.viewportPaddingIsBleed) return viewportWidth;

      const padding = this.measureViewportInlinePadding();

      return Math.max(0, viewportWidth - padding.start - padding.end);
    }

    /**
     * How far content may pass the content edge before any of it is hidden.
     *
     * A scroll container clips at its padding box, so content that spills past
     * its content edge stays on screen until it reaches the viewport's own
     * edge — it simply occupies the page margin that padding reserves. A rail
     * overflowing by less than that margin therefore hides nothing, and
     * enhancing it hands the customer arrows for content already in front of
     * them. The trailing margin is the boundary, not the content edge. A bleed
     * viewport has already counted that padding as width its content may
     * occupy, so granting it a second time here would double it. Pure read.
     *
     * @returns {number} Overflow, in pixels, that must be exceeded to enhance.
     */
    measureOverflowThreshold() {
      if (this.viewportPaddingIsBleed) return defaults.overflowTolerance;

      return Math.max(defaults.overflowTolerance, this.measureViewportInlinePadding().end);
    }

    /**
     * Travel each edge may still hold before any slide is off screen.
     *
     * The viewport's inline padding is margin it already draws, so travel that
     * only carries a slide into that margin hides nothing — except where a
     * clip-path takes the margin back, which is why the clip inset is subtracted
     * rather than assumed to be zero. The shared tolerance rides along so a
     * fractional layout cannot leave an arrow behind for a rounding pixel.
     * Pure read.
     *
     * @returns {{start: number, end: number}} Travel, in pixels, each edge may hold with its control hidden.
     */
    measureEdgeAllowance() {
      const styles = window.getComputedStyle(this.viewport);
      const clip = parseInlineInset(styles.clipPath);
      const drawnMargin = (padding, inset) =>
        Math.max(0, (Number.parseFloat(padding) || 0) - inset) + defaults.overflowTolerance;

      return {
        start: drawnMargin(styles.paddingInlineStart, clip.start),
        end: drawnMargin(styles.paddingInlineEnd, clip.end),
      };
    }

    /**
     * Re-measures the edge allowance and reports whether it moved.
     *
     * @returns {boolean} Whether either edge's allowance changed since the last measurement.
     */
    syncEdgeAllowance() {
      const next = this.measureEdgeAllowance();
      if (next.start === this.edgeAllowance.start && next.end === this.edgeAllowance.end) {
        return false;
      }

      this.edgeAllowance = next;
      return true;
    }

    /**
     * The owner's own slides, never a fill clone.
     *
     * Read from the track's children rather than from `instance.slides`, because
     * loop mode relocates slides across the ends of the track and this must stay
     * the merchant's set whatever order the vendor has moved it into.
     *
     * @returns {HTMLElement[]} Slides the owner rendered.
     */
    getSourceSlides() {
      if (!(this.track instanceof HTMLElement)) return [];

      return Array.from(this.track.children).filter(
        (element) => element.matches('.swiper-slide') && !element.hasAttribute(loopCloneAttribute)
      );
    }

    getFillClones() {
      if (!(this.track instanceof HTMLElement)) return [];

      return Array.from(this.track.querySelectorAll(`[${loopCloneAttribute}]`));
    }

    measureOverflow() {
      if (!this.requiresOverflow) {
        const slides = this.track instanceof HTMLElement
          ? Array.from(this.track.children).filter((element) => element.matches('.swiper-slide'))
          : [];
        return slides.length > 1;
      }

      const viewportContentWidth = this.measureViewportContentWidth();
      if (viewportContentWidth <= 0) return false;

      const trackWidth = this.track instanceof HTMLElement ? this.track.clientWidth : viewportContentWidth;
      const availableWidth = Math.min(viewportContentWidth, trackWidth || viewportContentWidth);
      const contentWidth = this.measureSourceContentWidth();

      return contentWidth - availableWidth > this.measureOverflowThreshold();
    }

    /**
     * Width of the owner's own content, excluding any fill clone.
     *
     * A filled track's `scrollWidth` counts the clones, so it can never fall
     * back under the overflow boundary and the rail would stay enhanced on a
     * viewport wide enough to hold every real slide. Summing the source slides
     * answers the same question about the merchant's content and is independent
     * of the order loop mode has left them in.
     *
     * @returns {number} Rendered width of the owner's slides plus their gaps.
     */
    measureSourceContentWidth() {
      const clones = this.loopFill ? this.getFillClones() : [];
      if (clones.length === 0) {
        return this.track instanceof HTMLElement ? this.track.scrollWidth : this.viewport.scrollWidth;
      }

      const sources = this.getSourceSlides();
      if (sources.length === 0) return 0;

      const total = sources.reduce((width, slide) => width + slide.getBoundingClientRect().width, 0);
      return total + this.resolveGap() * (sources.length - 1);
    }

    /**
     * Resolves how many slides the track needs before loop mode is seamless.
     *
     * Swiper's own floor is `slidesPerView + slidesPerGroup + loopAdditionalSlides`,
     * and below it the vendor warns and the rail stalls at the wrap instead of
     * continuing. That floor leaves no slide outside the visible window, so the
     * relocation lands in view as a jump; a whole viewport of slack either side
     * keeps it off-screen. `slidesPerView: 'auto'` has no authored visible count,
     * so it is bounded here from the narrowest rendered slide — the most slide
     * starts that can fall inside one viewport at any scroll position, which is
     * the largest value the vendor's own `slidesPerViewDynamic()` can report.
     *
     * Pure read.
     *
     * @param {number} slidesPerGroup - The owner's resolved `slidesPerGroup`.
     * @returns {number} Total slides the filled track should carry, or `0` to leave it alone.
     */
    measureLoopFillTarget(slidesPerGroup = 1) {
      const sources = this.getSourceSlides();
      if (sources.length < 2) return 0;

      const viewportContentWidth = this.measureViewportContentWidth();
      if (viewportContentWidth <= 0) return 0;

      let narrowest = Infinity;
      sources.forEach((slide) => {
        const width = slide.getBoundingClientRect().width;
        if (width > 0 && width < narrowest) narrowest = width;
      });
      if (!Number.isFinite(narrowest) || narrowest <= 0) return 0;

      const gap = this.resolveGap();
      const visible = 1 + Math.floor(viewportContentWidth / (narrowest + gap));
      const relocated = Math.max(1, slidesPerGroup) + defaults.loopAdditionalSlides;
      const required = visible * defaults.loopViewportFactor + relocated;

      // Repeated whole sets, so the owner's rendered rhythm — alternating
      // heights, aspect ratios, ordering — continues across the seam instead of
      // restarting part-way through it.
      return sources.length * Math.max(1, Math.ceil(required / sources.length));
    }

    /**
     * Whether the owner left centered snapping to the controller.
     *
     * Only a snapping `slidesPerView: 'auto'` rail qualifies: free mode has no
     * anchored active slide, a numeric `slidesPerView` sizes slides from the
     * integer viewport width rather than CSS, and a non-slide effect owns its
     * own translate semantics. An explicit `centeredSlides` in either direction
     * is an owner decision the controller must not overrule.
     *
     * @param {object} options - The owner's resolved Swiper options.
     * @returns {boolean} Whether geometry may decide centered snapping.
     */
    resolveCenteredSnapEligibility(options) {
      if (options.slidesPerView !== 'auto') return false;
      if (options.centeredSlides !== undefined) return false;
      if (options.freeMode?.enabled === true) return false;
      if (options.effect !== undefined && options.effect !== 'slide') return false;
      return true;
    }

    /**
     * Whether the rendered geometry calls for centered snapping.
     *
     * True only for the fractional 1.x-per-view presentation: a partial slide
     * peeks past the viewport and two whole slides do not fit. Wider rails keep
     * start-aligned snapping — centering there clamps several near-edge snap
     * positions onto the same translate, which turns arrow presses into dead
     * steps. Below two-per-view that collapse is impossible: the second snap
     * position stays strictly past the first for any slide/gap mix, so every
     * press moves. Measured from the narrowest rendered slide so a mixed-width
     * rail only centers when no pair of its slides fits. Pure read.
     *
     * @returns {boolean} Whether the active slide should snap to the midline.
     */
    measureCenteredSnap() {
      const viewportContentWidth = this.measureViewportContentWidth();
      if (viewportContentWidth <= 0) return false;

      const sources = this.getSourceSlides();
      if (sources.length < 2) return false;

      let narrowest = Infinity;
      sources.forEach((slide) => {
        const width = slide.getBoundingClientRect().width;
        if (width > 0 && width < narrowest) narrowest = width;
      });
      if (!Number.isFinite(narrowest) || narrowest <= 0) return false;

      if (viewportContentWidth - narrowest <= defaults.overflowTolerance) return false;

      return narrowest * 2 + this.resolveGap() - viewportContentWidth > defaults.overflowTolerance;
    }

    /**
     * Brings the track up to `targetCount` slides by repeating the owner's set.
     *
     * Must run before the instance is constructed: `loopCreate` indexes the
     * slides it finds, so a clone added afterwards is invisible to loop mode.
     * Pure write.
     *
     * @param {number} targetCount - Total slides the track should carry.
     * @returns {boolean} Whether the track's slide set changed.
     */
    applyLoopFill(targetCount) {
      if (!(this.track instanceof HTMLElement)) return false;

      const sources = this.getSourceSlides();
      const existing = this.getFillClones();
      if (sources.length === 0) return false;
      if (targetCount === sources.length + existing.length) return false;

      existing.forEach((clone) => clone.remove());
      if (targetCount <= sources.length) return existing.length > 0;

      const fragment = document.createDocumentFragment();
      for (let index = sources.length; index < targetCount; index += 1) {
        const clone = sources[index % sources.length].cloneNode(true);
        clone.setAttribute(loopCloneAttribute, '');
        clone.setAttribute('aria-hidden', 'true');
        // A clone is not the merchant's block: leaving the editor attribute on it
        // would give the theme editor several elements claiming one block id.
        clone.removeAttribute('data-shopify-editor-block');
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll(frame.focusableSelector).forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        clone.querySelectorAll('img').forEach((image) => {
          image.loading = 'lazy';
          image.fetchPriority = 'low';
        });
        fragment.append(clone);
      }

      this.track.append(fragment);
      return true;
    }

    removeLoopFill() {
      const clones = this.getFillClones();
      clones.forEach((clone) => clone.remove());
      this.loopFillTarget = 0;
      return clones.length > 0;
    }

    /**
     * Whether the viewport sits within the activation margin of the screen. Pure read.
     *
     * @returns {boolean} Whether the rail is close enough to enhance without waiting for intersection.
     */
    measureNearViewport() {
      const bounds = this.viewport.getBoundingClientRect();
      return bounds.bottom >= -400 && bounds.top <= window.innerHeight + 400;
    }

    scheduleActivation(isNearViewport) {
      if (!this.mounted || !this.overflows || this.instance || this.loadingPromise) return;
      if (this.idleHandle !== undefined) return;

      const nearViewport =
        isKeyboardModality() || (isNearViewport ?? this.measureNearViewport());
      if (!nearViewport && sharedIntersectionObserver) {
        intersectionTargets.set(this.viewport, this);
        sharedIntersectionObserver.observe(this.viewport);
        return;
      }

      if (!nearViewport) return;

      if (typeof window.requestIdleCallback === 'function') {
        this.idleHandleType = 'idle';
        this.idleHandle = window.requestIdleCallback(this.handleIntent, {
          timeout: 1500,
        });
      } else {
        this.idleHandleType = 'timeout';
        this.idleHandle = window.setTimeout(this.handleIntent, 300);
      }
    }

    promote() {
      if (!this.mounted || this.instance || this.loadingPromise) return this.loadingPromise;

      if (!this.measureOverflow()) {
        this.overflows = false;
        this.publishOverflow(false);
        this.cancelActivation();
        this.setState('static');
        return Promise.resolve(undefined);
      }

      this.overflows = true;
      this.publishOverflow(true);
      this.cancelActivation();
      this.setState('pending');
      this.loadingPromise = loadVendor()
        .then(
          () => {
            this.loadFailed = false;
            if (!this.mounted) return;
            if (!this.measureOverflow()) {
              this.overflows = false;
              this.publishOverflow(false);
              this.setState('static');
              return;
            }
            this.initializeInstance();
          },
          (error) => {
            this.loadFailed = true;
            throw error;
          }
        )
        .catch((error) => {
          if (!this.mounted) return;
          this.setState('error', error);
          if (this.loadFailed && isKeyboardModality()) scheduleKeyboardRetry();
        })
        .finally(() => {
          this.loadingPromise = undefined;
        });

      return this.loadingPromise;
    }

    initializeInstance(initialSlideOverride) {
      if (this.instance || typeof window.Swiper !== 'function') return;

      const ownerOptions =
        typeof this.options === 'function' ? this.options(this) || {} : this.options;
      const ownerEvents = ownerOptions.on || {};

      this.centeredSnapEligible = this.resolveCenteredSnapEligibility(ownerOptions);
      const centeredSnap = this.centeredSnapEligible && this.measureCenteredSnap();
      // A centered loop centers its initial slide too, which would reveal the
      // track's tail to the left of the static layout's flush first slide the
      // moment enhancement lands. So a loop owner that engages centered
      // snapping starts bounded — no loop, no fill clones, the bounds clamp —
      // and the loop is engaged by `engageCenteredLoop` once the customer has
      // navigated somewhere the two geometries agree.
      const deferLoop =
        centeredSnap && ownerOptions.loop === true && !this.centeredLoopEngaged;

      if (this.loopFill && ownerOptions.loop === true && !deferLoop) {
        const target = this.measureLoopFillTarget(ownerOptions.slidesPerGroup ?? 1);
        if (target > 0) {
          this.applyLoopFill(target);
          this.loopFillTarget = target;
        }
      }

      const focusedSlideIndex = this.getFocusedSlideIndex();
      let initialSlide;
      if (focusedSlideIndex >= 0) initialSlide = focusedSlideIndex;
      else if (typeof initialSlideOverride === 'number') initialSlide = initialSlideOverride;
      else initialSlide = ownerOptions.initialSlide ?? this.getVisibleSlideIndex();
      const controller = this;
      const updateEvents = ['init', 'slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'lock', 'unlock'];
      const events = { ...ownerEvents };

      updateEvents.forEach(function (eventName) {
        events[eventName] = function (...args) {
          controller.updateControls(args[0]);
          const ownerHandler = ownerEvents[eventName];
          if (typeof ownerHandler === 'function') ownerHandler.apply(this, args);
        };
      });

      if (deferLoop) {
        const ownerTransitionEnd = events.slideChangeTransitionEnd;
        events.slideChangeTransitionEnd = function (...args) {
          controller.engageCenteredLoop(args[0]);
          if (typeof ownerTransitionEnd === 'function') ownerTransitionEnd.apply(this, args);
        };
      }

      const parameters = {
        ...ownerOptions,
        initialSlide,
        speed: resolveMotionDuration(),
        watchOverflow: defaults.watchOverflow,
        noSwipingSelector: this.isNestedScroller
          ? defaults.noSwipingSelector
          : `${defaults.noSwipingSelector}, ${defaults.nestedScrollerSelector}`,
        updateOnWindowResize: defaults.updateOnWindowResize,
        resizeObserver: defaults.resizeObserver,
        on: events,
      };

      if (this.gapProperty) {
        this.gap = this.resolveGap();
        parameters.spaceBetween = this.gap;
      }

      // Fractional 1.x-per-view rails snap the active slide to the viewport's
      // midline. `centeredSlidesBounds` clamps the snap grid so the first and
      // last slides stay flush at the rail's ends; an engaged loop has no ends
      // for it to clamp against, and the vendor does not support the pair, so
      // it centers every slide instead.
      if (centeredSnap) {
        parameters.centeredSlides = true;
        if (deferLoop) parameters.loop = false;
        if (parameters.loop !== true) parameters.centeredSlidesBounds = true;
      }

      // Only a filled owner, because widening the relocation band also raises the
      // vendor's own minimum slide count. A filled track is sized from this exact
      // value so it always clears that minimum; an unfilled loop owner keeps the
      // vendor default and its own smaller minimum.
      if (this.loopFill && parameters.loop === true) {
        if (ownerOptions.loopAdditionalSlides === undefined) {
          parameters.loopAdditionalSlides = defaults.loopAdditionalSlides;
        }

        // `slidePrev` decides its step by looking the current translate up in the
        // snap grid with `indexOf`, and its `prevIndex` falls back to `0` when
        // that misses — the rail jumps to the beginning instead of stepping back
        // one. After a loop relocation the compensating translate is derived
        // arithmetically while the grid is measured separately, so against slides
        // sized by a CSS aspect ratio the two land a fraction of a pixel apart and
        // the lookup misses for some positions and not others. The controls step
        // by slide index instead, which needs no position lookup at all.
        this.loopNavigation = true;
      }

      if (this.mousewheelEnabled) parameters.mousewheel = { ...mousewheelDefaults };
      else parameters.mousewheel = false;

      if (
        !this.loopNavigation
        && this.previousButton instanceof HTMLButtonElement
        && this.nextButton instanceof HTMLButtonElement
      ) {
        parameters.navigation = {
          prevEl: this.previousButton,
          nextEl: this.nextButton,
        };
      } else {
        parameters.navigation = false;
      }

      const nativeScrollLeft = this.viewport.scrollLeft;
      try {
        this.captureAuthorInlineStyles();
        this.viewport.scrollLeft = 0;
        this.instance = new window.Swiper(this.viewport, parameters);
        if (this.loopNavigation) this.applyLoopNavigation();
        this.setState('enhanced');
        this.alignToFocusedSlide();
        this.updateControls(this.instance);
      } catch (error) {
        this.instance = undefined;
        this.restoreAuthorInlineStyles();
        this.removeLoopFill();
        this.viewport.scrollLeft = nativeScrollLeft;
        this.setState('error', error);
      }
    }

    /**
     * Converts a deferred centered loop into the real one.
     *
     * Called on every settled slide change of a bounded owner whose loop is
     * deferred. Bounded and looping centered snapping place a middle slide
     * identically, so a landing there is the moment the loop can be engaged
     * without the customer seeing any movement; the two end slides are the
     * positions the modes disagree on, so a landing there keeps the bounded
     * grid and waits for the next change. The rebuild is deferred to the
     * shared write phase rather than run inside the vendor's own transition
     * event, where destroying the emitting instance is unsafe.
     *
     * @param {object} instance - The bounded instance reporting the landing.
     * @returns {void}
     */
    engageCenteredLoop(instance) {
      if (this.centeredLoopEngaged || this.centeredLoopPending) return;
      if (!instance || instance.destroyed || instance !== this.instance) return;

      this.centeredLoopPending = true;
      frame.write(() => {
        this.centeredLoopPending = false;
        if (!this.mounted) return;

        const live = this.instance;
        if (!live || live.destroyed || live.animating) return;
        if (live.activeIndex <= 0 || live.activeIndex >= live.slides.length - 1) return;

        this.centeredLoopEngaged = true;
        this.rebuildInstance();
      });
    }

    /**
     * Rebuilds a filled loop instance around a larger slide set.
     *
     * `loopCreate` indexes the slides present when it runs, so a track that has
     * outgrown its fill — the viewport widened until more slides are visible
     * than the fill was sized for — cannot be topped up in place. The instance
     * is rebuilt on the slide the customer was looking at.
     *
     * @returns {void}
     */
    rebuildInstance() {
      const instance = this.instance;
      if (!instance || instance.destroyed) return;

      const loops = instance.params?.loop === true;
      const sourceCount = this.getSourceSlides().length;
      const currentIndex = (loops ? instance.realIndex : instance.activeIndex) || 0;

      instance.destroy(true, true);
      this.instance = undefined;
      this.restoreAuthorInlineStyles();
      this.removeLoopFill();
      this.initializeInstance(sourceCount > 0 ? currentIndex % sourceCount : 0);
    }

    captureAuthorInlineStyles() {
      const elements = [
        this.viewport,
        this.track,
        ...this.viewport.querySelectorAll('.swiper-slide'),
      ];

      this.authorInlineStyles = elements
        .filter((element, index) => element instanceof HTMLElement && elements.indexOf(element) === index)
        .map((element) => ({
          element,
          value: element.getAttribute('style'),
        }));
    }

    restoreAuthorInlineStyles() {
      this.authorInlineStyles.forEach(({ element, value }) => {
        if (!(element instanceof HTMLElement)) return;
        if (value === null) element.removeAttribute('style');
        else element.setAttribute('style', value);
      });
      this.authorInlineStyles = [];
    }

    getVisibleSlideIndex() {
      if (this.viewport.scrollLeft <= defaults.overflowTolerance) return 0;

      const slides = Array.from(this.viewport.querySelectorAll('.swiper-slide'));
      let visibleIndex = 0;
      slides.forEach((slide, index) => {
        if (slide.offsetLeft <= this.viewport.scrollLeft + defaults.overflowTolerance) {
          visibleIndex = index;
        }
      });
      return visibleIndex;
    }

    getFocusedSlideIndex() {
      const activeElement = document.activeElement;
      if (!(activeElement instanceof Element)) return -1;
      if (this.pointerFocusPending || activeElement === this.pointerFocusedElement) return -1;
      if (!activeElement.matches(':focus-visible')) return -1;

      if (!(this.track instanceof HTMLElement)) return -1;

      return Array.from(this.track.children)
        .filter((element) => element.matches('.swiper-slide'))
        .findIndex((slide) => slide.contains(activeElement));
    }

    alignToFocusedSlide() {
      if (!this.instance || this.instance.destroyed) return;

      const slideIndex = this.getFocusedSlideIndex();
      if (slideIndex < 0) return;

      this.viewport.scrollLeft = 0;
      if (this.instance.activeIndex !== slideIndex) this.instance.slideTo(slideIndex, 0);
      if (isKeyboardModality()) this.fitFocusedSlide(slideIndex);
    }

    /**
     * Nudges the track so the keyboard-focused slide and its ring sit inside the drawn viewport.
     *
     * @param {number} slideIndex - Index of the focused slide among the track's slides.
     * @returns {void}
     */
    fitFocusedSlide(slideIndex) {
      const instance = this.instance;
      if (!instance || instance.destroyed || instance.isLocked || instance.rtlTranslate) return;

      const slide = instance.slides?.[slideIndex];
      if (!(slide instanceof HTMLElement) || !slide.contains(document.activeElement)) return;

      const ring = frame.resolveCustomProperty(document.documentElement, '--focus-ring-width') || 2;
      const clip = parseInlineInset(window.getComputedStyle(this.viewport).clipPath);
      const view = this.viewport.getBoundingClientRect();
      const start = Math.max(view.left + clip.start, 0) + ring;
      const end = Math.min(view.right - clip.end, document.documentElement.clientWidth) - ring;
      const box = slide.getBoundingClientRect();
      if (box.width > end - start) return;

      let delta = 0;
      if (box.right > end) delta = box.right - end;
      if (box.left - delta < start) delta = box.left - start;
      if (Math.abs(delta) <= defaults.overflowTolerance) return;

      const next = Math.min(
        instance.minTranslate(),
        Math.max(instance.maxTranslate(), instance.translate - delta)
      );
      if (Math.abs(next - instance.translate) <= defaults.overflowTolerance) return;

      instance.translateTo(next, 0, false, false);
      instance.updateProgress();
      instance.updateActiveIndex();
      instance.updateSlidesClasses();
    }

    handlePointerDown() {
      this.pointerFocusPending = true;
      if (this.pointerFocusResetTimer !== undefined) {
        window.clearTimeout(this.pointerFocusResetTimer);
      }
      this.pointerFocusResetTimer = window.setTimeout(() => {
        this.pointerFocusPending = false;
        this.pointerFocusResetTimer = undefined;
      });
    }

    handleFocusIn(event) {
      this.pointerFocusedElement =
        this.pointerFocusPending && event.target instanceof Element
          ? event.target
          : undefined;
      const landedOnPendingViewport = event.target === this.viewport && this.state === 'pending';
      const promotion = this.promote();
      if (landedOnPendingViewport && promotion) {
        promotion.then(() => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => this.recoverViewportFocus());
          });
        });
      }
      this.alignToFocusedSlide();
      if (this.focusFrame) return;

      this.focusFrame = window.requestAnimationFrame(() => {
        this.focusFrame = undefined;
        if (!this.mounted) return;
        if (Math.abs(this.viewport.scrollLeft) > defaults.overflowTolerance) {
          this.alignToFocusedSlide();
        }
      });
    }

    /**
     * Moves keyboard focus that the promoted viewport dropped onto the rail's first enabled control.
     *
     * @returns {void}
     */
    recoverViewportFocus() {
      if (!this.mounted || !isKeyboardModality()) return;

      const active = document.activeElement;
      if (active && active !== document.body) return;

      const control = [this.previousButton, this.nextButton].find(function (button) {
        return button instanceof HTMLButtonElement && button.isConnected && !button.hidden && !button.disabled;
      });
      control?.focus({ preventScroll: true });
    }

    handleKeyDown() {
      const wasPointerFocused = document.activeElement === this.pointerFocusedElement;
      this.pointerFocusedElement = undefined;
      if (wasPointerFocused) this.alignToFocusedSlide();
    }

    handleWheel(event) {
      if (!this.overflows) return;

      const hasHorizontalIntent = this.resolveWheelIntent(event);

      if (this.instance && !hasHorizontalIntent) {
        event.stopImmediatePropagation();
        return;
      }

      if (hasHorizontalIntent && !this.instance) this.promote();
    }

    /**
     * Resolves whether one wheel event belongs to a horizontal rail gesture.
     *
     * A trackpad swipe is a single continuous gesture whose per-event deltas
     * ramp up from a pixel or two, peak, and taper back down, drifting a
     * little vertically the whole way. Testing every event against the bar
     * that decides whether to claim the gesture therefore discards its start,
     * its tail, and every gentle swipe outright. So the bar decides only
     * whether the rail may claim the gesture at all — ordinary vertical
     * scrolling is still never consumed — and once claimed the gesture stays
     * claimed until the wheel falls quiet or the fingers turn decisively
     * vertical, which hands the page back its scroll mid-swipe.
     *
     * @param {WheelEvent} event - Wheel event received on the viewport.
     * @returns {boolean} Whether the rail should act on this event.
     */
    resolveWheelIntent(event) {
      const horizontalDelta = Math.abs(event.deltaX);
      const verticalDelta = Math.abs(event.deltaY);

      if (verticalDelta > horizontalDelta * defaults.wheelDominanceRatio) {
        this.wheelGestureTime = -Infinity;
        return false;
      }

      if (event.timeStamp - this.wheelGestureTime < defaults.wheelGestureTimeout) {
        this.wheelGestureTime = event.timeStamp;
        // Swiper's own `forceToAxis` still arbitrates a mixed-axis event, and
        // releases it to the page when the vertical component wins.
        return horizontalDelta > 0;
      }

      if (
        horizontalDelta < defaults.wheelEngageDelta ||
        horizontalDelta < verticalDelta * defaults.wheelDominanceRatio
      ) {
        return false;
      }

      this.wheelGestureTime = event.timeStamp;
      return true;
    }

    /**
     * Freezes swiping while a pinch is in progress or the page is zoomed in.
     *
     * @param {TouchEvent} event - Touch lifecycle event on the owner.
     * @returns {void}
     */
    handlePinch(event) {
      const instance = this.instance;
      if (!instance || instance.destroyed) return;

      const zoomed = (window.visualViewport?.scale ?? 1) > 1.01;
      if (event.touches.length > 1 || (zoomed && event.type === 'touchstart')) {
        if (this.pinchLocked) return;
        this.pinchLocked = true;
        instance.allowTouchMove = false;
        const touch = instance.touchEventsData;
        if (touch?.isMoved) {
          touch.isTouched = false;
          touch.isMoved = false;
          instance.slideTo(instance.activeIndex);
        }
        return;
      }

      const released = event.touches.length === 0 || event.type === 'touchstart';
      if (this.pinchLocked && released && !zoomed) {
        this.pinchLocked = false;
        instance.allowTouchMove = instance.params.allowTouchMove !== false;
      }
    }

    /**
     * Steps a looping filled rail one slide, by index rather than by position.
     *
     * Dormant unless `initializeInstance` declined to hand the buttons to the
     * vendor's navigation module, so an ordinary rail keeps that module's
     * behavior untouched.
     *
     * @param {MouseEvent} event - Click on one of the owner's controls.
     * @returns {void}
     */
    handleLoopControlClick(event) {
      if (!this.loopNavigation) return;

      const instance = this.instance;
      if (!instance || instance.destroyed) return;

      const total = instance.slides.length;
      if (total === 0) return;

      event.preventDefault();
      if (event.currentTarget === this.previousButton) instance.slidePrev();
      else instance.slideNext();
    }

    /**
     * Gives a filled loop instance's `slidePrev` the shape its `slideNext` uses.
     *
     * `slideNext` relocates for the direction and then steps `activeIndex +
     * slidesPerGroup`. `slidePrev` instead looks the current translate up in the
     * snap grid with `indexOf`, and its `prevIndex` falls back to `0` when that
     * misses — the rail jumps to the beginning rather than stepping back one.
     * The lookup misses intermittently once a loop relocation has derived the
     * translate arithmetically against aspect-ratio slide widths, so only the
     * backward direction is affected and only at some positions.
     *
     * Only `slidePrev` is replaced. It is the one method with the defect, and it
     * has to be replaced on the instance rather than at the arrow, because the
     * mousewheel module calls `slidePrev()` directly too. Stepping by DOM index
     * also keeps the travel direction honest: resolving the target by slide data
     * index can land on an element the relocation has left behind the current
     * one, which animates the rail backwards on a forward press.
     *
     * @returns {void}
     */
    applyLoopNavigation() {
      const instance = this.instance;
      if (!instance || instance.destroyed) return;

      instance.slidePrev = (speed, runCallbacks = true, internal) => {
        if (instance.destroyed || !instance.enabled) return instance;
        if (instance.animating && instance.params.loopPreventsSliding) return false;

        const step = instance.params.slidesPerGroup || 1;
        instance.loopFix({ direction: 'prev' });
        // The vendor reads this between the relocation and the step; its
        // transition path expects the forced reflow to have happened.
        instance._clientLeft = instance.wrapperEl.clientLeft;

        return instance.slideTo(
          instance.activeIndex - step,
          speed === undefined ? instance.params.speed : speed,
          runCallbacks,
          internal
        );
      };
    }

    handleMotionPreference() {
      if (!this.instance || this.instance.destroyed) return;

      const duration = resolveMotionDuration();
      this.instance.params.speed = duration;
      this.instance.originalParams.speed = duration;
    }

    handleFallbackResize() {
      if (this.fallbackResizeFrame) return;

      this.fallbackResizeFrame = window.requestAnimationFrame(() => {
        this.fallbackResizeFrame = undefined;
        holdActiveSlides([this]);
        this.refresh(false);
      });
    }

    updateControls(instance) {
      const controlsMayRender = Boolean(instance && !instance.destroyed && !instance.isLocked);
      const locked = !controlsMayRender;
      const loops = !locked && instance.params?.loop === true;
      const activeControl =
        document.activeElement === this.previousButton || document.activeElement === this.nextButton
          ? document.activeElement
          : undefined;
      const travel = locked ? undefined : this.resolveRemainingTravel(instance);
      const previousAvailable = !locked && (loops || travel.start > this.edgeAllowance.start);
      const nextAvailable = !locked && (loops || travel.end > this.edgeAllowance.end);

      this.setControl(this.previousButton, previousAvailable, controlsMayRender);
      this.setControl(this.nextButton, nextAvailable, controlsMayRender);

      if (
        activeControl instanceof HTMLButtonElement &&
        (activeControl.hidden || activeControl.disabled)
      ) {
        const fallback = activeControl === this.previousButton ? this.nextButton : this.previousButton;
        if (fallback instanceof HTMLButtonElement && !fallback.hidden && !fallback.disabled) {
          fallback.focus({ preventScroll: true });
        }
      }
    }

    /**
     * How far the rail can still travel towards each end from where it rests.
     *
     * Derived from `progress` rather than from `instance.translate`, which the
     * vendor commits *after* it emits the edge events this reads on — so a
     * control updated from the translate answers for the position the rail just
     * left. `progress` is assigned in the same pass that sets `isBeginning` and
     * `isEnd`, so it is exactly as current as the booleans it replaces.
     *
     * @param {object} instance - Live Swiper instance.
     * @returns {{start: number, end: number}} Remaining travel towards each end, in pixels.
     */
    resolveRemainingTravel(instance) {
      const total = Math.abs(instance.maxTranslate() - instance.minTranslate());
      const progress = Number.isFinite(instance.progress) ? instance.progress : 0;
      // A drag past an end rubberbands the progress outside its own range.
      const travelled = total * Math.min(1, Math.max(0, progress));

      return { start: travelled, end: total - travelled };
    }

    setControl(button, available, controlsMayRender = false) {
      if (!(button instanceof HTMLButtonElement)) return;

      const showDisabled =
        this.unavailableControls === 'disabled' && (controlsMayRender || this.state === 'pending');

      button.hidden = !available && !showDisabled;
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
    }

    publishOverflow(overflows, scrollLeft) {
      const next = overflows ? 'true' : 'false';
      if (this.viewport.dataset.swiperOverflow === next) return;

      this.viewport.dataset.swiperOverflow = next;
      if (!overflows && (scrollLeft ?? this.viewport.scrollLeft) !== 0) this.viewport.scrollLeft = 0;
    }

    setState(state, error) {
      if (this.state === state && !error) return;

      this.state = state;
      this.stateTarget.dataset.swiperState = state;
      if (state !== 'enhanced') this.updateControls(undefined);
      if (typeof this.onStateChange === 'function') {
        this.onStateChange({ state, error, controller: this });
      }
    }

    cancelActivation() {
      if (sharedIntersectionObserver) sharedIntersectionObserver.unobserve(this.viewport);
      intersectionTargets.delete(this.viewport);

      if (this.idleHandle !== undefined) {
        if (this.idleHandleType === 'idle') window.cancelIdleCallback(this.idleHandle);
        else window.clearTimeout(this.idleHandle);
      }
      this.idleHandle = undefined;
      this.idleHandleType = undefined;
    }

    destroyInstance() {
      if (!this.instance) return;

      // Loop mode leaves the track in a rotated order and reports the active
      // slide by `realIndex`; `loopDestroy` puts the rendered order back during
      // `destroy`, so that is the index the restored track is addressed by.
      const loops = this.instance.params?.loop === true;
      const activeIndex = (loops ? this.instance.realIndex : this.instance.activeIndex) || 0;
      this.instance.destroy(true, true);
      this.instance = undefined;
      this.restoreAuthorInlineStyles();

      // Clones leave from the end of the restored order, so removing them never
      // renumbers the owner's own slides. An active clone maps back to the slide
      // it repeats.
      this.loopNavigation = false;
      this.centeredSnapEligible = false;
      this.centeredLoopEngaged = false;
      this.centeredLoopPending = false;
      const sourceCount = this.loopFill ? this.getSourceSlides().length : 0;
      const removedFill = this.loopFill ? this.removeLoopFill() : false;
      const restoreIndex = removedFill && sourceCount > 0 ? activeIndex % sourceCount : activeIndex;

      frame.read(() => {
        if (!this.mounted || this.instance) return;

        const activeSlide = this.viewport.querySelectorAll('.swiper-slide')[restoreIndex];
        if (!(activeSlide instanceof HTMLElement)) return;

        const offset = activeSlide.offsetLeft;
        frame.write(() => {
          if (!this.mounted || this.instance) return;
          this.viewport.scrollLeft = offset;
        });
      });
    }

    destroy() {
      if (!this.mounted) return;

      this.mounted = false;
      mountedControllers.delete(this);
      this.cancelActivation();
      if (this.measurementFrame) frame.cancelMeasure(this);
      if (this.focusFrame) window.cancelAnimationFrame(this.focusFrame);
      if (this.fallbackResizeFrame) window.cancelAnimationFrame(this.fallbackResizeFrame);
      if (this.pointerFocusResetTimer !== undefined) {
        window.clearTimeout(this.pointerFocusResetTimer);
      }
      this.measurementFrame = undefined;
      this.focusFrame = undefined;
      this.fallbackResizeFrame = undefined;
      this.pointerFocusResetTimer = undefined;
      this.forceUpdatePending = false;
      this.measuredWidth = undefined;
      this.measuredHeight = undefined;
      resizeQueue.delete(this);
      settleQueue.delete(this);

      if (sharedResizeObserver) {
        sharedResizeObserver.unobserve(this.viewport);
        resizeTargets.delete(this.viewport);
        if (this.track) {
          sharedResizeObserver.unobserve(this.track);
          resizeTargets.delete(this.track);
        }
      } else {
        window.removeEventListener('resize', this.handleFallbackResize);
      }

      this.owner.removeEventListener('pointerenter', this.handleIntent);
      this.owner.removeEventListener('pointerdown', this.handlePointerDown, true);
      this.owner.removeEventListener('focusin', this.handleFocusIn);
      this.owner.removeEventListener('keydown', this.handleKeyDown, true);
      this.owner.removeEventListener('touchstart', this.handleIntent);
      for (const type of ['touchstart', 'touchend', 'touchcancel']) {
        this.owner.removeEventListener(type, this.handlePinch, true);
      }
      this.pinchLocked = false;
      if (this.previousButton instanceof HTMLButtonElement) {
        this.previousButton.removeEventListener('click', this.handleLoopControlClick);
      }
      if (this.nextButton instanceof HTMLButtonElement) {
        this.nextButton.removeEventListener('click', this.handleLoopControlClick);
      }
      this.viewport.removeEventListener('wheel', this.handleWheel);
      reducedMotion?.removeEventListener('change', this.handleMotionPreference);
      this.destroyInstance();
      this.pointerFocusPending = false;
      this.pointerFocusedElement = undefined;
      this.wheelGestureTime = -Infinity;
      this.overflows = false;
      delete this.viewport.dataset.swiperOverflow;
      this.setState('static');
    }
  }

  window.DieuxSwiper = Object.freeze({
    defaults,
    resolveMotionDuration,
    mount: function (configuration) {
      return new SwiperController(configuration).mount();
    },
  });

  window.dispatchEvent(new CustomEvent('dieux:swiper:ready'));
})();
